import { parseComplexCSV } from '@utils/csvParser.js';
import { parseShoplineV1Xlsx } from '@utils/xlsxParser.js';
import { generateJsonSchema as createJsonSchema } from '@utils/schemaGenerator.js';
import { generateLlmsTxt as createLlmsTxt } from '@utils/llmsTxtGenerator.js';
import { generateNdjson as createNdjson } from '@utils/ndjsonGenerator.js';
import { downloadFile, formatBaseUrl, isValidUrl } from '@utils/fileHandler.js';
import JSZip from 'jszip';

/**
 * CsvConverter Alpine component
 * Handles CSV file upload, parsing and conversion
 */
export function csvConverter() {
  return {
    fileName: null,
    parsedData: [],
    rawData: [], // Raw data for preview (original column structure)
    headers: [],
    jsonSchema: '',
    llmsTxt: '',
    ndjsonContent: '',
    isDragging: false,
    storeUrl: 'https://example.com',
    storeUrlError: '',
    csvFormat: null, // 'shopline' | 'shopify' | 'shopline-v1' | null
    // Progress bar related state
    processing: false,
    processingProgress: 0,
    processingStage: '',
    // Config panel state
    showConfigPanel: false,
    // Batch mode related state
    batchMode: false,
    multipleFiles: [],
    batchProgress: 0,
    batchCurrentFile: '',
    // Copy button animation states
    copiedJson: false,
    copiedLlms: false,
    copiedNdjson: false,

    /**
     * Validate if URL is valid
     * @param {string} value - Value to validate
     * @returns {boolean} - Whether it's a valid URL
     */
    isValidUrl(value) {
      return isValidUrl(value);
    },

    /**
     * Formatted base URL (computed property)
     */
    get formattedBaseUrl() {
      return formatBaseUrl(this.storeUrl);
    },

    /**
     * Whether has data (computed property)
     */
    get hasData() {
      return this.rawData.length > 0;
    },

    /**
     * Whether has JSON Schema (computed property)
     */
    get hasJsonSchema() {
      return this.jsonSchema.length > 0;
    },

    /**
     * Whether has llms.txt (computed property)
     */
    get hasLlmsTxt() {
      return this.llmsTxt.length > 0;
    },

    /**
     * Whether has NDJSON (computed property)
     */
    get hasNdjson() {
      return this.ndjsonContent.length > 0;
    },

    /**
     * Initialize global drag/drop listeners for the drop zone.
     * Prevents the browser from navigating away on file drop.
     */
    initDropZone() {
      const hasFiles = (event) =>
        event?.dataTransfer && [...event.dataTransfer.types || []].includes('Files');

      const handleWindowDragOver = (event) => {
        if (!hasFiles(event)) {return;}
        event.preventDefault();
        this.isDragging = true;
      };

      const handleWindowDragLeave = (event) => {
        if (!hasFiles(event)) {return;}
        if (event.relatedTarget === null) {
          this.isDragging = false;
        }
      };

      const handleWindowDrop = (event) => {
        if (!hasFiles(event)) {return;}
        event.preventDefault();
        this.isDragging = false;
      };

      window.addEventListener('dragover', handleWindowDragOver);
      window.addEventListener('dragleave', handleWindowDragLeave);
      window.addEventListener('drop', handleWindowDrop);

      return () => {
        window.removeEventListener('dragover', handleWindowDragOver);
        window.removeEventListener('dragleave', handleWindowDragLeave);
        window.removeEventListener('drop', handleWindowDrop);
      };
    },

    /**
     * Handle file selection
     */
    handleFileSelect(event) {
      if (this.batchMode) {
        this.handleMultipleFilesSelect(event);
      } else {
        const file = event.target.files[0];
        this.processFile(file);
      }
    },

    /**
     * Handle drag and drop
     */
    handleDrop(event) {
      this.isDragging = false;
      if (this.batchMode && event.dataTransfer.files.length > 1) {
        this.handleMultipleFilesDrop(event);
      } else {
        const file = event.dataTransfer.files[0];
        this.processFile(file);
      }
    },

    /**
     * Handle multiple files selection
     */
    handleMultipleFilesSelect(event) {
      const files = [...event.target.files];
      this.processMultipleFiles(files);
    },

    /**
     * Handle multiple files drag and drop
     */
    handleMultipleFilesDrop(event) {
      const files = [...event.dataTransfer.files];
      this.processMultipleFiles(files);
    },

    /**
     * Process multiple files
     */
    async processMultipleFiles(files) {
      const validFiles = files.filter(f =>
        f.name.toLowerCase().endsWith('.csv') || f.name.toLowerCase().endsWith('.xlsx')
      );

      if (validFiles.length === 0) {
        alert('Please select at least one CSV or XLSX file');
        return;
      }

      this.multipleFiles = validFiles.map(f => ({
        data: null,
        error: null,
        name: f.name,
        status: 'pending'
      }));

      this.processing = true;
      let allParsedData = [];
      let allHeaders = new Set();
      let detectedFormat = 'unknown';

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        this.batchCurrentFile = file.name;
        this.batchProgress = Math.round((i / validFiles.length) * 100);
        this.processingStage = `Processing ${file.name} (${i + 1}/${validFiles.length})...`;

        try {
          const isXlsx = file.name.toLowerCase().endsWith('.xlsx');

          if (isXlsx) {
            // Process XLSX file
            const arrayBuffer = await file.arrayBuffer();
            const result = parseShoplineV1Xlsx(arrayBuffer);

            this.multipleFiles[i].status = 'completed';
            this.multipleFiles[i].data = result.parsedData;

            allParsedData = allParsedData.concat(result.parsedData);
            result.headers.forEach(h => allHeaders.add(h));

            // Use first detected format
            if (detectedFormat === 'unknown') {
              detectedFormat = result.csvFormat || 'shopline-v1';
            }
          } else {
            // Process CSV file
            const csv = await this.readFileAsText(file);
            const result = parseComplexCSV(csv);

            this.multipleFiles[i].status = 'completed';
            this.multipleFiles[i].data = result.parsedData;

            allParsedData = allParsedData.concat(result.parsedData);
            result.headers.forEach(h => allHeaders.add(h));

            // Use first detected format
            if (detectedFormat === 'unknown') {
              detectedFormat = result.csvFormat || 'unknown';
            }
          }
        } catch (error) {
          this.multipleFiles[i].status = 'error';
          this.multipleFiles[i].error = error.message;
        }
      }

      this.batchProgress = 100;
      this.processingStage = 'Generating outputs...';

      // Merge all data
      this.parsedData = allParsedData;
      this.headers = [...allHeaders];
      this.fileName = `${validFiles.length} files merged`;
      this.csvFormat = detectedFormat;

      // Generate outputs
      this.generateJsonSchema();
      this.generateLlmsTxt();
      this.generateNdjson();

      this.processing = false;
      this.batchCurrentFile = '';
      this.batchProgress = 0;
      this.processingStage = '';
    },

    /**
     * Read file as text
     */
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });
    },

    /**
     * Process file
     */
    async processFile(file) {
      if (!file) {
        alert('Please select a file');
        return;
      }

      const fileName = file.name.toLowerCase();
      const isCsv = fileName.endsWith('.csv');
      const isXlsx = fileName.endsWith('.xlsx');

      if (!isCsv && !isXlsx) {
        alert('Please select a valid CSV or XLSX file');
        return;
      }

      this.fileName = file.name;
      this.processing = true;
      this.processingProgress = 0;
      this.processingStage = 'Reading file...';

      if (isXlsx) {
        // Process XLSX file
        await this.processXlsxFile(file);
      } else {
        // Process CSV file
        const reader = new FileReader();
        reader.onload = async (e) => {
          const csv = e.target.result;
          await this.parseCsv(csv);
        };
        reader.readAsText(file);
      }
    },

    /**
     * Process XLSX file
     */
    async processXlsxFile(file) {
      try {
        this.processingStage = 'Reading XLSX file...';
        this.processingProgress = 10;

        const arrayBuffer = await file.arrayBuffer();

        this.processingStage = 'Parsing XLSX file...';
        this.processingProgress = 30;
        await new Promise(resolve => setTimeout(resolve, 50));

        const result = parseShoplineV1Xlsx(arrayBuffer);
        this.parsedData = result.parsedData;
        this.rawData = result.rawData || result.parsedData; // Use rawData for preview, fallback to parsedData
        this.headers = result.headers;
        this.csvFormat = result.csvFormat || 'shopline-v1';

        this.processingProgress = 50;
        this.processingStage = 'Generating JSON Schema...';
        await new Promise(resolve => setTimeout(resolve, 50));
        this.generateJsonSchema();

        this.processingProgress = 70;
        this.processingStage = 'Generating llms.txt...';
        await new Promise(resolve => setTimeout(resolve, 50));
        this.generateLlmsTxt();

        this.processingProgress = 90;
        this.processingStage = 'Generating NDJSON...';
        await new Promise(resolve => setTimeout(resolve, 50));
        this.generateNdjson();

        this.processingProgress = 100;
        this.processingStage = 'Complete!';
        await new Promise(resolve => setTimeout(resolve, 300));
        this.processing = false;
        this.processingProgress = 0;
        this.processingStage = '';
      } catch (error) {
        console.error('Error parsing XLSX:', error);
        this.processing = false;
        this.processingProgress = 0;
        this.processingStage = '';
        alert('Error parsing XLSX file. Please check the file format.\n\nDetails: ' + error.message);
      }
    },

    /**
     * Parse CSV
     */
    async parseCsv(csv) {
      try {
        this.processingStage = 'Parsing CSV file...';
        this.processingProgress = 20;

        // Use setTimeout to allow UI to update
        await new Promise(resolve => setTimeout(resolve, 50));

        const result = parseComplexCSV(csv);
        this.parsedData = result.parsedData;
        this.rawData = result.parsedData; // CSV data is already in raw format
        this.headers = result.headers;
        this.csvFormat = result.csvFormat || 'unknown';

        this.processingProgress = 40;
        this.processingStage = 'Generating JSON Schema...';
        await new Promise(resolve => setTimeout(resolve, 50));
        this.generateJsonSchema();

        this.processingProgress = 60;
        this.processingStage = 'Generating llms.txt...';
        await new Promise(resolve => setTimeout(resolve, 50));
        this.generateLlmsTxt();

        this.processingProgress = 80;
        this.processingStage = 'Generating NDJSON...';
        await new Promise(resolve => setTimeout(resolve, 50));
        this.generateNdjson();

        this.processingProgress = 100;
        this.processingStage = 'Complete!';
        await new Promise(resolve => setTimeout(resolve, 300));
        this.processing = false;
        this.processingProgress = 0;
        this.processingStage = '';
      } catch (error) {
        console.error('Error parsing CSV:', error);
        this.processing = false;
        this.processingProgress = 0;
        this.processingStage = '';
        alert('Error parsing CSV file. Please check the file format.\n\nDetails: ' + error.message);
      }
    },

    /**
     * Generate JSON Schema
     */
    generateJsonSchema() {
      this.jsonSchema = createJsonSchema(this.parsedData, this.csvFormat);
    },

    /**
     * Generate llms.txt
     */
    generateLlmsTxt() {
      this.llmsTxt = createLlmsTxt(this.parsedData, this.formattedBaseUrl, this.csvFormat);
    },

    /**
     * Generate NDJSON
     */
    generateNdjson() {
      this.ndjsonContent = createNdjson(
        this.parsedData,
        this.formattedBaseUrl,
        this.csvFormat
      );
    },

    /**
     * Download JSON Schema
     */
    downloadJsonSchema() {
      downloadFile(this.jsonSchema, 'product-schema.json', 'application/json');
    },

    /**
     * Download llms.txt
     */
    downloadLlmsTxt() {
      downloadFile(this.llmsTxt, 'llms.txt', 'text/plain');
    },

    /**
     * Download NDJSON
     */
    downloadNdjson() {
      downloadFile(this.ndjsonContent, 'openai-commerce-feed.ndjson', 'application/x-ndjson');
    },

    /**
     * Download all files as ZIP
     */
    async downloadAllAsZip() {
      try {
        const zip = new JSZip();

        // Add all output files
        zip.file('product-schema.json', this.jsonSchema);
        zip.file('llms.txt', this.llmsTxt);
        zip.file('openai-commerce-feed.ndjson', this.ndjsonContent);

        // Add README file
        const readmeContent = `SkuSync Export Package
===================

Export Time: ${new Date().toLocaleString('en-US')}
Source File: ${this.fileName || 'Unknown'}
Data Rows: ${this.parsedData.length}

File Description:
---------
- product-schema.json: JSON Schema format product data definition
- llms.txt: LLM-optimized context file (following llmstxt.org specification)
- openai-commerce-feed.ndjson: OpenAI Product Feed Specification compliant NDJSON

Usage:
---------
1. JSON Schema can be used for data validation and API documentation
2. llms.txt can be deployed to website root for AI crawler discovery
3. openai-commerce-feed.ndjson follows OpenAI Product Feed Specification for AI-powered commerce

For more information visit: https://skusync.app

---
SkuSync - Instantly Transform SHOPLINE Data for AI-Powered eCommerce
`;
        zip.file('README.txt', readmeContent);

        // Generate ZIP file
        const blob = await zip.generateAsync({ type: 'blob' });

        // Download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `skusync-export-${Date.now()}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error creating ZIP file:', error);
        alert('Error creating ZIP file: ' + error.message);
      }
    },

    /**
     * Reset file
     */
    resetFile() {
      // Reset all data-related state
      Object.assign(this, {
        batchCurrentFile: '',
        batchProgress: 0,
        csvFormat: null,
        fileName: null,
        headers: [],
        jsonSchema: '',
        llmsTxt: '',
        multipleFiles: [],
        ndjsonContent: '',
        parsedData: [],
        rawData: []
      });

      // Clear file input
      const fileInput = document.getElementById('fileInput');
      if (fileInput) {
        fileInput.value = '';
      }
    },

    /**
     * Toggle batch mode
     */
    toggleBatchMode() {
      this.batchMode = !this.batchMode;
      this.resetFile();
    },

    /**
     * Copy JSON Schema to clipboard with animation feedback
     */
    async copyJsonSchema() {
      try {
        await navigator.clipboard.writeText(this.jsonSchema);
        this.copiedJson = true;
        setTimeout(() => { this.copiedJson = false; }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
        alert('Failed to copy to clipboard');
      }
    },

    /**
     * Copy llms.txt to clipboard with animation feedback
     */
    async copyLlmsTxt() {
      try {
        await navigator.clipboard.writeText(this.llmsTxt);
        this.copiedLlms = true;
        setTimeout(() => { this.copiedLlms = false; }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
        alert('Failed to copy to clipboard');
      }
    },

    /**
     * Copy NDJSON to clipboard with animation feedback
     */
    async copyNdjson() {
      try {
        await navigator.clipboard.writeText(this.ndjsonContent);
        this.copiedNdjson = true;
        setTimeout(() => { this.copiedNdjson = false; }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
        alert('Failed to copy to clipboard');
      }
    },

    /**
     * Validate store URL
     */
    validateStoreUrl() {
      const isEmpty = !this.storeUrl;
      const isExample = this.storeUrl === 'https://example.com';
      const isInvalid = !isEmpty && !isExample && !isValidUrl(this.storeUrl);

      this.storeUrlError = isEmpty
        ? 'Store URL is required'
        : (isInvalid
        ? 'Please enter a valid URL (e.g., https://yourstore.com)'
        : '');
    }
  };
}
